import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  TextField, 
  Typography, 
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Card,
  CardMedia,
  Grid,
  Tooltip,
  Divider,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import { useNavigate, useLocation } from 'react-router-dom';
import { ROUTES } from '../config/routes';
import Layout from '../components/Layout';
import MultiImageUploader from '../components/MultiImageUploader';
import TagsInput from '../components/TagsInput';
import TagExtractionStatus from '../components/TagExtractionStatus';
import itemService from '../services/itemService';
import boxService from '../services/boxService';
// import api, { directApi } from '../services/api'; // Unused in current implementation
import offlineApi from '../services/offlineApi';
import networkStatus from '../services/networkStatus';
import CreateBoxDialog from '../components/CreateBoxDialog';
import tagTasksService from '../services/tagTasksService';
import { resizeImageFiles } from '../utils/imageResizeUtils';
import { placeholderImage } from '../utils/imageUtils';
import demoService from '../services/demoService';
import aiService from '../services/aiService';
import VoiceInputButton from '../components/VoiceInputButton';
import { mergeTagsString } from '../utils/tagMerge';

// Import styling from our design system
import { colors } from '../styles/styleConstants';


/**
 * Simplified AddItem component with single-screen approach
 * Focuses on quick image uploads with minimal required fields
 */
const AddItem = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [boxes, setBoxes] = useState([]);
  const [selectedBox, setSelectedBox] = useState('');
  const [files, setFiles] = useState([]);
  // Use a ref to store file references to prevent issues with state updates
  const fileRef = React.useRef([]);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [successCount, setSuccessCount] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [previewMode, setPreviewMode] = useState(false);
  const [useDefaultItemNames, setUseDefaultItemNames] = useState(false);
  
  // New box creation dialog visibility (fields + logic live in CreateBoxDialog)
  const [createBoxOpen, setCreateBoxOpen] = useState(false);
  const [isStructuringVoice, setIsStructuringVoice] = useState(false);

  useEffect(() => {
    // Log that the component has mounted
    console.log('AddItem component mounted');
    
    const checkOfflineStatus = async () => {
      const isOffline = await networkStatus.shouldUseOfflineMode();
      // Log the offline mode status
      console.log(`AddItem component is in ${isOffline ? 'offline' : 'online'} mode`);
    };
    checkOfflineStatus();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const boxId = params.get('boxId');
    if (boxId) {
      setSelectedBox(boxId);
    }
    // Load name preference setting
    const savedNamePreference = localStorage.getItem('useDefaultItemNames');
    if (savedNamePreference !== null) {
      setUseDefaultItemNames(savedNamePreference === 'true');
    }
  }, [location]);

  // Fetch boxes on component mount
  useEffect(() => {
    fetchBoxes();
  }, []);
  
  // Check for preselected box after boxes are loaded
  useEffect(() => {
    if (boxes.length > 0 && !selectedBox) {
      const preselectedBoxId = localStorage.getItem('preselected_box_id');
      if (preselectedBoxId) {
        console.log('AddItem - Found preselected box ID:', preselectedBoxId);
        setSelectedBox(preselectedBoxId);
        // Clear the preselected box ID after using it
        localStorage.removeItem('preselected_box_id');
      }
    }
  }, [boxes, selectedBox]);
  
  // Function to fetch boxes
  const fetchBoxes = async () => {
    try {
      // Check if we should use offline mode
      const useOfflineMode = await networkStatus.shouldUseOfflineMode();
      console.log(`AddItem - Fetching boxes with ${useOfflineMode ? 'offline' : 'online'} mode`);
      
      let boxesData = [];
      
      if (useOfflineMode) {
        // Get boxes from offline storage
        boxesData = await offlineApi.getAllBoxes();
        console.log('AddItem - Got boxes from offline storage:', boxesData.length);
      } else {
        // Get boxes from API
        boxesData = await boxService.getAllBoxes();
        console.log('AddItem - Got boxes from API:', boxesData.length);
      }
      
      setBoxes(Array.isArray(boxesData) ? boxesData : []);
    } catch (error) {
      console.error('Error fetching boxes:', error);
      setError('Failed to load boxes. Please try again.');
      
      // Attempt to load from offline storage as a fallback
      try {
        const offlineBoxes = await offlineApi.getAllBoxes();
        if (Array.isArray(offlineBoxes) && offlineBoxes.length > 0) {
          console.log('AddItem - Using offline boxes as fallback');
          setBoxes(offlineBoxes);
          setError('Using offline data. Some information might not be up-to-date.');
        }
      } catch (fallbackError) {
        console.error('Error fetching offline boxes as fallback:', fallbackError);
      }
    }
  };
  
  // Handle creation of a new box
  const handleBoxCreated = async (box) => {
    await fetchBoxes();
    if (box && box.id) setSelectedBox(box.id);
  };

  const handleVoiceTranscript = async (transcript) => {
    try {
      setIsStructuringVoice(true);
      const result = await aiService.structureTranscript(transcript, name || null);
      setDescription((result && result.description) || transcript);
      if (result && Array.isArray(result.tags) && result.tags.length) {
        setTags((prev) => mergeTagsString(prev, result.tags));
      }
    } catch (err) {
      console.error('Voice structuring failed, using raw transcript:', err);
      setDescription(transcript);
    } finally {
      setIsStructuringVoice(false);
    }
  };

  // Handle file selection
  const handleFilesSelect = async (selectedFiles) => {
    if (selectedFiles && selectedFiles.length > 0) {
      console.log(`Selected ${selectedFiles.length} files`);
      
      // Resize images immediately upon selection
      setIsLoading(true);
      try {
        console.log('Resizing images after selection...');
        // Resize images to 640x640 max dimensions while maintaining aspect ratio
        const resizedFiles = await resizeImageFiles(selectedFiles, {
          maxWidth: 640,
          maxHeight: 640,
          format: 'jpeg',
          quality: 0.85
        });
        
        if (resizedFiles && resizedFiles.length > 0) {
          console.log(`Successfully resized ${resizedFiles.length} images`);
          setFiles(resizedFiles);
          fileRef.current = resizedFiles;
        } else {
          console.warn('Image resizing did not return any files, using originals');
          setFiles(selectedFiles);
          fileRef.current = selectedFiles;
        }
      } catch (resizeError) {
        console.error('Error resizing images:', resizeError);
        // Continue with original files if resizing fails
        console.warn('Using original unresized files due to resize error');
        setFiles(selectedFiles);
        fileRef.current = selectedFiles;
      } finally {
        setIsLoading(false);
      }
      
      setPreviewMode(true);
    } else {
      setFiles([]);
      fileRef.current = [];
      setError('No files selected');
    }
    console.log('Files stored in ref:', fileRef.current);
  
    // Clear any previous errors
    setError(null);
    
    // Always show preview mode first so user can review and add details
    setPreviewMode(true);
  };

  // Handle upload of files
  const handleUpload = async () => {
    // Get files from both state and ref to ensure we have them
    const filesToUpload = files.length > 0 ? files : fileRef.current;
    console.log('Handling upload with files:', filesToUpload);
    if (!selectedBox) {
      setError('Please select a box first');
      return;
    }
    if (!filesToUpload || filesToUpload.length === 0) {
      setError('Please select at least one image');
      return;
    }
    
    // Check demo mode limits
    if (demoService.isDemoMode()) {
      const remaining = demoService.getRemainingItemSlots();
      if (remaining !== null && filesToUpload.length > remaining) {
        if (remaining === 0) {
          setError(`Demo limit reached! You've used all ${demoService.getDemoInfo()?.item_limit || 20} items. Sign up free to continue.`);
        } else {
          setError(`Demo limit: You can only add ${remaining} more item${remaining === 1 ? '' : 's'}. Sign up free for unlimited items.`);
        }
        return;
      }
    }
    
    setIsLoading(true);
    setError(null);
    setSuccess(false);
    setSuccessCount(0);
    
    // Images have already been resized during selection
    console.log('Using pre-resized images for upload...');
    
    try {
      // Check if we should use offline mode
      const useOfflineMode = await networkStatus.shouldUseOfflineMode();
      console.log(`AddItem - Uploading files with ${useOfflineMode ? 'offline' : 'online'} mode`);
      
      // Upload files one by one to show progress
      const total = filesToUpload.length;
      let completed = 0;
      const successfulUploads = [];
      
      for (const file of filesToUpload) {
        try {
          // Prepare item data
          const fileName = file.name.split('.')[0];
          let itemName = name;
          
          // If no name is provided, use filename or default name based on settings
          if (!itemName) {
            itemName = useDefaultItemNames 
              ? 'New Item' 
              : fileName || 'New Item';
          }
          
          // Keep track for UI
          console.log(`Uploading ${total === 1 ? 'item' : `item ${completed + 1}/${total}`}: ${itemName}`);
          
          // Create item with metadata
          const itemData = {
            name: itemName,
            description: description || '',
            box_id: selectedBox,
            custom_tags: tags || ''
          };
          
          let itemResponse;
          
          if (useOfflineMode) {
            // In offline mode, store the item in IndexedDB
            // Convert file to base64 for offline storage
            const fileData = await new Promise((resolve) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result);
              reader.readAsDataURL(file);
            });
            
            // Create offline item data with the file content
            const offlineItemData = {
              ...itemData,
              // Store file data in a format recognizable when offline
              _offline_file: {
                content: fileData,
                name: file.name,
                type: file.type,
                size: file.size
              },
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            };
            
            // Save to offline API
            itemResponse = await offlineApi.addOrUpdateItem(offlineItemData);
            console.log('AddItem - Created item using offline API:', itemResponse);
            
            // Queue for syncing when back online
            await offlineApi.queueRequest({
              method: 'POST',
              url: '/items',
              data: {
                ...itemData,
                _file_to_upload: file.name, // just a reference, file will be re-uploaded later
                _offline_item_id: itemResponse.id // to remove it from offline storage after sync
              },
              timestamp: Date.now(),
              type: 'item_create_with_file'
            });
            console.log('AddItem - Queued item creation for later sync');
            
            // Update box item count
            const boxToUpdate = await offlineApi.getBoxById(selectedBox);
            if (boxToUpdate) {
              const currentCount = (boxToUpdate.items_count || boxToUpdate.item_count || 0);
              await offlineApi.updateBox(selectedBox, {
                items_count: currentCount + 1,
                item_count: currentCount + 1
              }
              );
            }
          } else {
            // Online mode - use regular API
            // Upload file and create item
            const formData = new FormData();
            formData.append('photo', file);  // Use 'photo' instead of 'file' to match the backend expectation
            
            // Add all item data to formData
            Object.keys(itemData).forEach(key => {
              formData.append(key, itemData[key]);
            });
            
            // Use the correct function name: uploadItem instead of createItemWithFile
            itemResponse = await itemService.uploadItem(formData);
          }
          
          // Add to successful uploads
          successfulUploads.push(itemResponse);
          
          // Automatically queue background tag extraction for the newly created item if online
          // Legacy tag extraction system disabled - now using automatic AI processing
          // The new system runs automatically in the background with status tracking
          if (!useOfflineMode && itemResponse && itemResponse.id) {
            console.log(`Item ${itemResponse.id} uploaded successfully. AI processing will run automatically in background.`);
            // AI processing status is now tracked via ai_processing_status field
            // No manual queueing needed - automatic_processing_service handles this
          }
          
          completed++;
          setUploadProgress(Math.round((completed / total) * 100));
          setSuccessCount(completed);
          
        } catch (itemError) {
          console.error('Error uploading specific item:', itemError);
          completed++;
          setUploadProgress(Math.round((completed / total) * 100));
        }
      }
      
      // Store upload result in localStorage for the Box page to display
      if (successfulUploads.length > 0) {
        // Only clear cache entries related to this specific box to ensure fresh images load
        try {
          // Import needed functions from imageCache
          const { clearCacheForBox, clearCacheForItem } = require('../utils/imageCache');
          
          // Get the IDs of newly uploaded items
          const newItemIds = successfulUploads.map(item => item.id);
          console.log(`Selectively clearing cache for box ${selectedBox} and ${newItemIds.length} new items`);
          
          // Clear cache for this specific box
          clearCacheForBox(selectedBox);
          
          // Clear cache for each new item individually
          let totalCleared = 0;
          newItemIds.forEach(itemId => {
            const cleared = clearCacheForItem(itemId);
            totalCleared += cleared;
          });
          
          // Store new item IDs for the BoxDetailModal to use
          localStorage.setItem('cubxly_new_item_ids', JSON.stringify(newItemIds));
          
          console.log(`Selectively cleared ${totalCleared} cache entries for box ${selectedBox} and ${newItemIds.length} new items`);
        } catch (cacheError) {
          console.warn('Error clearing image cache:', cacheError);
          // Continue even if cache clearing fails
        }
        
        localStorage.setItem('cubxly_upload_success', JSON.stringify({
          count: successfulUploads.length,
          boxId: selectedBox,
          timestamp: Date.now(),
          itemIds: successfulUploads.map(item => item.id)
        }));
        
        // Update demo item count if in demo mode
        if (demoService.isDemoMode()) {
          // Check feature completion status BEFORE marking anything
          const demoInfo = demoService.getDemoInfo();
          const uploadImageWasCompleted = demoInfo?.features_to_try?.find(f => f.id === 'upload_image')?.completed;
          console.log('[Demo] upload_image was already completed:', uploadImageWasCompleted);
          
          demoService.updateDemoItemCount(successfulUploads.length);
          // Mark create_box as completed (user selected a box to add items to)
          demoService.markFeatureCompleted('create_box');
          // Mark upload_image as completed
          demoService.markFeatureCompleted('upload_image');
          
          // If upload_image was already completed before this upload, this is a subsequent upload
          // Mark multi_item as completed (user is trying the multi-item feature)
          if (uploadImageWasCompleted) {
            console.log('[Demo] Marking multi_item as completed (subsequent upload)');
            demoService.markFeatureCompleted('multi_item');
          }
        }
        
        // Navigate immediately to the box page without showing success here
        const boxDetailPath = ROUTES.BOX_DETAIL(selectedBox);
        navigate(boxDetailPath);
        console.log(`Navigating to box page: ${boxDetailPath}`);
      }
      
    } catch (error) {
      console.error('Error uploading items:', error);
      setError(`Failed to upload items: ${error.message}. Please try again.`);
    } finally {
      setIsLoading(false);
    }
  };

  // Reset preview mode
  const handleBack = () => {
    setPreviewMode(false);
  };

  return (
    <Layout title="Add Item" onBack={() => window.history.back()}>
      {/* Alerts for feedback */}
    {error && (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    )}
    
    {success && (
      <Alert 
        severity="success" 
        sx={{ 
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          '& .MuiAlert-message': { flex: 1 }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <Typography variant="body1">
            <strong>{successCount} item{successCount !== 1 ? 's' : ''} uploaded successfully!</strong> Redirecting to box...
          </Typography>
          <CircularProgress size={20} thickness={5} />
        </Box>
      </Alert>
    )}
      <Paper sx={{ p: 3, mb: 3 }}>
        {isLoading ? (
          /* Upload progress UI */
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <CircularProgress size={50} sx={{ mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Uploading {successCount}/{files.length} {files.length === 1 ? 'Image' : 'Images'}
            </Typography>
            <Box 
              sx={{ 
                width: '100%', 
                height: 8, 
                bgcolor: 'grey.200', 
                borderRadius: 4,
                mt: 2,
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <Box 
                sx={{ 
                  width: `${uploadProgress}%`, 
                  height: '100%', 
                  bgcolor: 'primary.main',
                  transition: 'width 0.3s ease-in-out',
                  borderRadius: 4
                }}
              />
            </Box>
          </Box>
        ) : success ? (
          /* Success UI */
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h5" color="primary" gutterBottom>
              Success!
            </Typography>
            <Typography variant="body1">
              {files.length === 1
                ? 'Your item has been added to the box.'
                : `${files.length} items have been added to the box.`}
            </Typography>
          </Box>
        ) : previewMode ? (
          /* Preview mode UI - show selected images and allow upload */
          <Box>
            <Typography variant="h6" gutterBottom>
              Ready to Add {files.length} {files.length === 1 ? 'Item' : 'Items'}
            </Typography>

            {/* Image previews */}
            <Box sx={{ mb: 3 }}>
              <Grid container spacing={1}>
                {files.map((file, index) => (
                  <Grid item xs={4} sm={3} md={2} key={index}>
                    <Card sx={{ borderRadius: 1, overflow: 'hidden' }}>
                      <CardMedia
                        component="img"
                        image={file instanceof File || file instanceof Blob ? URL.createObjectURL(file) : placeholderImage}
                        alt={`Image ${index + 1}`}
                        height={100}
                        sx={{ objectFit: 'cover' }}
                      />
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Box selection */}
            <FormControl fullWidth margin="normal" required>
              <InputLabel id="box-select-label">Box</InputLabel>
              <Select
                labelId="box-select-label"
                id="box-select"
                value={selectedBox || ''}
                label="Box"
                onChange={(e) => setSelectedBox(e.target.value)}
              >
                {Array.isArray(boxes) && boxes.map((box) => (
                  <MenuItem key={box.id} value={box.id}>
                    {box.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* AI auto-population info */}
            <Alert 
              severity="info" 
              sx={{ 
                mt: 2,
                mb: 2, 
                borderRadius: 2,
                backgroundColor: 'rgba(59, 130, 246, 0.08)',
                '& .MuiAlert-icon': { color: colors.actionBlue }
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                AI-Powered Auto-Population
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                After upload, AI will automatically generate descriptions and searchable tags.
                {files.length > 1 && ' Multiple items in a single photo will be detected and saved separately.'}
              </Typography>
            </Alert>

            {/* Box selection is the only required field for bulk uploads */}
            {/* Optional details are hidden by default and will be editable later */}
            {files.length === 1 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Uploading a single item. You can add details now or edit later.
                </Typography>
                <Button 
                  size="small" 
                  variant="outlined" 
                  color="primary"
                  onClick={() => document.getElementById('optional-details').style.display = document.getElementById('optional-details').style.display === 'none' ? 'block' : 'none'}
                  sx={{ mb: 2 }}
                >
                  Show/Hide Details
                </Button>
                
                <Box id="optional-details" sx={{ display: 'none', mt: 2, mb: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Optional Details
                  </Typography>

                  <Tooltip title={useDefaultItemNames ? "If left empty, a default name will be generated" : "Name can be left empty"} placement="top">
                    <TextField
                      fullWidth
                      margin="dense"
                      label="Item Name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      size="small"
                      placeholder={useDefaultItemNames ? "Leave empty for default name" : "Optional"}
                      helperText={
                        useDefaultItemNames 
                          ? "Leave empty to auto-generate name (can be changed in Settings)"
                          : "Optional - can be added later"
                      }
                    />
                  </Tooltip>

                  <TextField
                    fullWidth
                    margin="dense"
                    label="Description"
                    multiline
                    rows={2}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    size="small"
                    disabled={isStructuringVoice}
                    helperText={isStructuringVoice ? 'Structuring your dictation…' : undefined}
                    InputProps={{
                      endAdornment: (
                        <VoiceInputButton
                          onInterim={(text) => setDescription(text)}
                          onTranscript={handleVoiceTranscript}
                          disabled={isStructuringVoice}
                          tooltip="Dictate description & tags"
                        />
                      ),
                    }}
                  />

                  <TagsInput
                    value={tags}
                    onChange={setTags}
                    compact={true}
                  />
                </Box>
              </Box>
            )}
            
            {files.length > 1 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold' }}>
                  Bulk upload mode: {files.length} items selected
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Details can be added after upload by clicking on individual items
                </Typography>
              </Box>
            )}

            {/* Action buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <Button
                variant="outlined"
                onClick={handleBack}
              >
                Back
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleUpload}
                disabled={!selectedBox}
              >
                Add to Box
              </Button>
            </Box>
          </Box>
        ) : (
          /* Initial UI - Image selection */
          <Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">
                Add Items to Box
              </Typography>
              
              {/* Offline indicator */}
              {!navigator.onLine && (
                <Chip 
                  icon={<CloudOffIcon />} 
                  label="Offline Mode" 
                  color="warning" 
                  variant="outlined"
                  sx={{ ml: 2 }}
                />
              )}
            </Box>

            {/* Box selection with option to create new box */}
            <FormControl 
              fullWidth 
              margin="normal" 
              required 
              error={!selectedBox} // Highlight when no box is selected
              sx={{
                '& .MuiOutlinedInput-root': {
                  ...((!selectedBox) && {
                    borderColor: 'error.main',
                    animation: 'pulse 1.5s infinite',
                    '@keyframes pulse': {
                      '0%': { boxShadow: '0 0 0 0 rgba(211, 47, 47, 0.4)' },
                      '70%': { boxShadow: '0 0 0 5px rgba(211, 47, 47, 0)' },
                      '100%': { boxShadow: '0 0 0 0 rgba(211, 47, 47, 0)' },
                    },
                  }),
                },
              }}
            >
              <InputLabel id="box-select-label">
                {!selectedBox ? 'Select a Box (Required)' : 'Box'}
              </InputLabel>
              <Select
                labelId="box-select-label"
                id="box-select"
                value={selectedBox || ''}
                label={!selectedBox ? 'Select a Box (Required)' : 'Box'}
                onChange={(e) => setSelectedBox(e.target.value)}
              >
                {Array.isArray(boxes) && boxes.map((box) => (
                  <MenuItem key={box.id} value={box.id}>
                    {box.name}
                  </MenuItem>
                ))}
                <Divider />
                <MenuItem onClick={() => setCreateBoxOpen(true)} sx={{ color: 'primary.main' }}>
                  <AddIcon sx={{ mr: 1 }} /> Create New Box
                </MenuItem>
              </Select>
              {!selectedBox && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                  Please select a box for this item
                </Typography>
              )}
            </FormControl>

            {/* Image selection component */}
            <MultiImageUploader
              onFilesSelect={handleFilesSelect}
              loading={isLoading}
              allowCamera={true}
              maxFiles={10}
            />

            {/* AI auto-population info */}
            <Alert 
              severity="info" 
              sx={{ 
                mt: 2, 
                borderRadius: 2,
                backgroundColor: 'rgba(59, 130, 246, 0.08)',
                '& .MuiAlert-icon': { color: colors.actionBlue }
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                AI-Powered Auto-Population
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                After upload, AI will automatically analyze your images and generate descriptions and searchable tags. 
                If your photo contains multiple items, each will be detected and saved separately.
              </Typography>
            </Alert>

            {selectedBox && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                Images will be added to: {Array.isArray(boxes) ? boxes.find(box => box.id === selectedBox)?.name || 'Selected Box' : 'Selected Box'}
              </Typography>
            )}
          </Box>
        )}
      </Paper>
      
      {/* Create-box dialog (shared component) */}
      <CreateBoxDialog
        open={createBoxOpen}
        onClose={() => setCreateBoxOpen(false)}
        onCreated={handleBoxCreated}
      />
      {/* Display tag extraction status for any background tasks */}
      <TagExtractionStatus itemId={null} />
    </Layout>
  );
  
};

export default AddItem;
